import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AttendeeHistoryResponse, clubsBookingApi } from '@/api/clubs-booking';
import AdminShell from '@/components/admin/AdminShell';
import { formatCurrencyFromCents } from '@/utils/currency';

const AttendeeOverviewPage = () => {
  const { attendeeId } = useParams<{ attendeeId: string }>();
  const [data, setData] = useState<AttendeeHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!attendeeId) return;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await clubsBookingApi.getMyAttendeeHistory(attendeeId);
        setData(response);
      } catch (err: any) {
        const messageText = err?.response?.data?.message;
        setError(Array.isArray(messageText) ? messageText.join(', ') : messageText || 'Could not load attendee history');
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [attendeeId]);

  const attendeeName = useMemo(() => {
    if (!data?.attendee) return 'Attendee';
    return data.attendee.fullName || `${data.attendee.firstName} ${data.attendee.lastName}`.trim();
  }, [data]);

  return (
    <AdminShell title={attendeeName} subtitle="Activity, session and booking history">
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="mb-4">
          <Link to="/attendees" className="text-sm text-[#1F8F5F] hover:underline">
            Back to attendees
          </Link>
        </div>

        {loading && <p className="text-sm text-slate-600">Loading attendee history...</p>}
        {!loading && error && <p className="text-sm text-red-600">{error}</p>}

        {!loading && !error && data && (
          <div className="space-y-6">
            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Parent</p>
                <p className="mt-1 font-medium text-slate-900">{data.attendee.parentName}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Bookings</p>
                <p className="mt-1 text-xl font-semibold text-slate-900">{data.summary.totalBookings}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Sessions booked</p>
                <p className="mt-1 text-xl font-semibold text-slate-900">{data.summary.totalSessionsBooked}</p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <p className="text-xs uppercase tracking-wide text-slate-500">Sessions attended</p>
                <p className="mt-1 text-xl font-semibold text-slate-900">{data.summary.totalSessionsAttended}</p>
              </div>
            </div>

            <div className="space-y-4">
              {data.history.length === 0 && (
                <p className="text-sm text-slate-600">No booking history found for this attendee yet.</p>
              )}

              {data.history.map((entry) => (
                <article key={entry.bookingId} className="rounded-lg border border-slate-200">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">
                        {entry.event?.title || 'Activity'}
                      </p>
                      <p className="text-xs text-slate-500">
                        Booking {entry.bookingReference} Â· {new Date(entry.createdAt).toLocaleString('en-GB')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${entry.paymentStatus === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {entry.paymentStatus}
                      </span>
                      <Link
                        to={`/bookings/${entry.bookingId}`}
                        className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium hover:bg-white"
                      >
                        View booking
                      </Link>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="mb-3 flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-700">
                      <p>
                        <span className="font-medium">Total:</span>{' '}
                        {formatCurrencyFromCents(entry.totalPriceCents, entry.currency)}
                      </p>
                      <p>
                        <span className="font-medium">Method:</span>{' '}
                        <span className="capitalize">{entry.paymentMethod}</span>
                      </p>
                      <p>
                        <span className="font-medium">Sessions:</span> {entry.sessionsBooked} booked, {entry.sessionsAttended} attended
                      </p>
                    </div>
                    <div className="overflow-x-auto rounded-lg border border-slate-200">
                      <table className="min-w-[760px] w-full text-left text-sm">
                        <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                          <tr>
                            <th className="px-3 py-2">Session</th>
                            <th className="px-3 py-2">Attendance</th>
                            <th className="px-3 py-2">Checked in</th>
                            <th className="px-3 py-2">Checked out</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {entry.sessions.map((session) => (
                            <tr key={`${entry.bookingId}-${session.sessionId}`}>
                              <td className="px-3 py-2 text-slate-900">
                                {session.startDateTime
                                  ? `${new Date(session.startDateTime).toLocaleString('en-GB')} - ${session.endDateTime ? new Date(session.endDateTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : ''}`
                                  : session.sessionId}
                              </td>
                              <td className="px-3 py-2 capitalize text-slate-700">{session.sessionAttendanceStatus.replace('_', ' ')}</td>
                              <td className="px-3 py-2 text-slate-700">{session.checkedInAt ? new Date(session.checkedInAt).toLocaleString('en-GB') : '-'}</td>
                              <td className="px-3 py-2 text-slate-700">{session.checkedOutAt ? new Date(session.checkedOutAt).toLocaleString('en-GB') : '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
    </AdminShell>
  );
};

export default AttendeeOverviewPage;

